import React, { FC, ReactNode, useMemo, useState } from "react";
import { HsChartIcon, HsLeftIcon, HsSettingsIcon, HsUsersIcon, OuSearch } from "../HubSpotUi";

/**
 * The dashboard's frame for HubSpot orgs, in the layout of HubSpot's own app
 * (docs/hubspot-ui-reference/screens): the dark navigation column under the
 * top bar (collapsible to its icons), the two framing a light grey content
 * area with a rounded top-left corner, and in it either a page on HubSpot's
 * white rounded canvas
 * (the reports dashboard) or HubSpot's settings layout, a white left panel of
 * grouped links beside the settings page (settings-left-panel.json). Styles
 * are in styles/hubspot.css. Outreach orgs never render any of it.
 */

export interface SettingsItem {
  key: string;
  label: string;
}

export interface SettingsGroup {
  heading: string;
  items: SettingsItem[];
}

/** The settings panel's groups, in HubSpot's order: your own, then the account's, then the tools. */
export const hubSpotSettingsGroups = (showUsers: boolean): SettingsGroup[] => [
  { heading: "Your Preferences", items: [{ key: "account", label: "Account" }] },
  {
    heading: "Account Management",
    items: [
      ...(showUsers ? [{ key: "users", label: "Users & Licenses" }] : []),
      { key: "licenses", label: "License Sync" },
      { key: "billing", label: "Billing" },
      { key: "addons", label: "Add-ons" },
    ],
  },
  {
    heading: "Tools",
    items: [
      { key: "integration-status", label: "Integration" },
      { key: "workflows", label: "Configuration" },
      { key: "export", label: "Export" },
      { key: "dialer", label: "Dialer" },
      { key: "linkedin", label: "LinkedIn" },
      { key: "ai-apps", label: "Connected AI apps" },
    ],
  },
];

export const hubSpotSettingsLabel = (key: string): string =>
  hubSpotSettingsGroups(true)
    .flatMap((g) => g.items)
    .find((i) => i.key === key)?.label ?? "Settings";

/* ── the navigation column ── */

/** A briefcase with an arrow, in the line style of the other navigation icons. */
const HsJobChangeIcon: FC = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth={2.28} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.4" y="9.1" width="25.2" height="18.3" rx="2.3" />
    <path d="M11.4 9.1V6.9a2.3 2.3 0 0 1 2.3-2.3h4.6a2.3 2.3 0 0 1 2.3 2.3v2.2" />
    <path d="M10.3 18.3h11.4M17.7 14.3l4 4-4 4" />
  </svg>
);

const NavItem: FC<{ icon: ReactNode; label: string; active: boolean; collapsed: boolean; onClick: () => void; badge?: ReactNode }> = ({ icon, label, active, collapsed, onClick, badge }) => (
  <li>
    <button
      type="button"
      className="hs-sidenav__item"
      aria-current={active ? "page" : undefined}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      onClick={onClick}
    >
      <span className="hs-sidenav__icon">{icon}</span>
      <span className="hs-sidenav__label">{label}</span>
      {badge}
    </button>
  </li>
);

/** Whether the navigation is collapsed, remembered per browser (a viewer convenience only). */
const NAV_COLLAPSED_KEY = "boring.hs.sidenavCollapsed";
const readCollapsed = () => {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

/** HubSpot's sidebar collapse mark, drawn in our line style: a panel with an arrow. */
const SidebarToggleIcon: FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth={2.28} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.5" y="5" width="25" height="22" rx="3" />
    <path d="M11 5v22" />
    <path d={collapsed ? "M17.5 12.5 21 16l-3.5 3.5" : "M21 12.5 17.5 16l3.5 3.5"} />
  </svg>
);

/** Pages with their own navigation item, outside the settings panel. */
export const HUBSPOT_TOP_PAGES = ["insights", "users", "job-changes"];

export const HubSpotSideNav: FC<{ page: string; onNavigate: (page: string) => void; showUsers: boolean; jobChangesBadge?: ReactNode }> = ({
  page,
  onNavigate,
  showUsers,
  jobChangesBadge,
}) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () =>
    setCollapsed((was) => {
      try {
        window.localStorage.setItem(NAV_COLLAPSED_KEY, was ? "0" : "1");
      } catch {
        // Private windows: the choice just isn't remembered.
      }
      return !was;
    });
  return (
    <nav className={`hs-sidenav${collapsed ? " is-collapsed" : ""}`} aria-label="boring">
      <ul>
        <NavItem icon={<HsChartIcon />} label="Insights" collapsed={collapsed} active={page === "insights"} onClick={() => onNavigate("insights")} />
        <NavItem
          icon={<HsJobChangeIcon />}
          label="Job changes"
          collapsed={collapsed}
          active={page === "job-changes"}
          onClick={() => onNavigate("job-changes")}
          badge={jobChangesBadge}
        />
        {showUsers && <NavItem icon={<HsUsersIcon />} label="Users" collapsed={collapsed} active={page === "users"} onClick={() => onNavigate("users")} />}
        <NavItem
          icon={<HsSettingsIcon />}
          label="Settings"
          collapsed={collapsed}
          active={!HUBSPOT_TOP_PAGES.includes(page)}
          onClick={() => onNavigate(!HUBSPOT_TOP_PAGES.includes(page) ? page : "account")}
        />
      </ul>
      <button
        type="button"
        className="hs-sidenav__toggle"
        aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        aria-expanded={!collapsed}
        title={collapsed ? "Expand navigation" : "Collapse navigation"}
        onClick={toggle}
      >
        <SidebarToggleIcon collapsed={collapsed} />
      </button>
    </nav>
  );
};

/* ── the settings layout ── */

export const HubSpotSettingsLayout: FC<{ page: string; onNavigate: (page: string) => void; showUsers: boolean; children: ReactNode }> = ({
  page,
  onNavigate,
  showUsers,
  children,
}) => {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return hubSpotSettingsGroups(showUsers)
      .map((g) => ({ ...g, items: g.items.filter((i) => !q || i.label.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [query, showUsers]);

  return (
    <div className="hs-settings">
      <aside className="hs-settings__panel" aria-label="Settings">
        <header className="hs-settings__panel-header">
          <button type="button" className="hs-settings__back" onClick={() => onNavigate("insights")}>
            <HsLeftIcon />
            Insights
          </button>
          <OuSearch variant="lg" value={query} onChange={setQuery} placeholder="Search Settings" width="100%" />
        </header>
        <div className="hs-settings__groups">
          {groups.map((g) => (
            <nav key={g.heading} className="hs-settings__group" aria-label={g.heading}>
              <h5 className="hs-settings__heading">{g.heading}</h5>
              <ul>
                {g.items.map((i) => (
                  <li key={i.key}>
                    <button type="button" className="hs-settings__item" aria-current={i.key === page ? "page" : undefined} onClick={() => onNavigate(i.key)}>
                      {i.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
          {groups.length === 0 && <p className="hs-settings__empty">No settings match “{query}”.</p>}
        </div>
      </aside>
      <main className="hs-settings__main">{children}</main>
    </div>
  );
};

/** HubSpot's boxed tabs, on their own (the settings page puts them over a full-width rule). */
export const HubSpotBoxedTabs: FC<{ tabs: { value: string; label: ReactNode }[]; tab?: string; onTab?: (value: string) => void } & Record<`data-${string}`, string>> = ({
  tabs,
  tab,
  onTab,
  ...rest
}) => (
  <div className="hs-settings-page__tablist" role="tablist" {...rest}>
    {tabs.map((t) => (
      <button key={t.value} type="button" role="tab" aria-selected={t.value === tab} className="hs-settings-page__tab" onClick={() => onTab?.(t.value)}>
        {t.label}
      </button>
    ))}
  </div>
);

/* ── a settings page: H1, boxed tabs, description and rule (settings-form-page.json) ── */

export const HubSpotSettingsPage: FC<{
  title: ReactNode;
  tabs?: { value: string; label: ReactNode }[];
  tab?: string;
  onTab?: (value: string) => void;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}> = ({ title, tabs, tab, onTab, description, actions, children }) => (
  <div className="hs-settings-page">
    <h2 className="hs-settings-page__title">{title}</h2>
    {tabs && tabs.length > 1 && (
      <div className="hs-settings-page__tabs">
        <HubSpotBoxedTabs tabs={tabs} tab={tab} onTab={onTab} />
      </div>
    )}
    {(description || actions) && (
      <div className="hs-settings-page__intro">
        {description && <p className="hs-settings-page__description">{description}</p>}
        {actions && <div className="hs-settings-page__actions">{actions}</div>}
      </div>
    )}
    {description && !actions && <hr className="hs-settings-page__rule" />}
    {children}
  </div>
);
